import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi } from './admin-api.ts';
import { Spinner, PrimaryButton } from './ui.tsx';
import { CustomQuestionsView } from './CustomQuestionsView.tsx';
import { groupBySubCategory } from './survey-config-grouping.ts';
import type { SurveyConfigDimension, SurveyConfigQuestion, LibraryQuestion } from './types.ts';

export function SurveyConfigView({ engagementId }: { engagementId: string }) {
  const queryClient = useQueryClient();
  const configQ = useQuery({
    queryKey: ['survey-config', engagementId],
    queryFn: () => adminApi.getSurveyConfig(engagementId),
  });
  // Global, not scoped to this engagement — Data Questions / Department-Specific
  // Questions are a single shared, admin-editable pool (see services/library-questions.ts).
  const libraryQ = useQuery({
    queryKey: ['library-questions'],
    queryFn: () => adminApi.getLibraryQuestions(),
  });
  // Same query key CustomQuestionsView uses below, so this is a cache hit, not a second fetch.
  const customQ = useQuery({
    queryKey: ['custom-questions', engagementId],
    queryFn: () => adminApi.getCustomQuestions(engagementId),
  });

  const [dims, setDims] = useState<SurveyConfigDimension[] | null>(null);
  const [questions, setQuestions] = useState<SurveyConfigQuestion[] | null>(null);
  const [dirty, setDirty] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);

  // Initialize state from query data
  if (configQ.data && dims === null) {
    setDims(configQ.data.dimensions);
    setQuestions(configQ.data.questions);
  }

  const saveMutation = useMutation({
    mutationFn: () => {
      const excludedDimIds = (dims ?? []).filter((d) => !d.included).map((d) => d.id);
      const excludedQIds = (questions ?? []).filter((q) => !q.included).map((q) => q.id);
      return adminApi.saveSurveyConfig(engagementId, excludedDimIds, excludedQIds);
    },
    onSuccess: () => {
      setDirty(false);
      queryClient.invalidateQueries({ queryKey: ['survey-config', engagementId] });
    },
  });

  const invalidateLibrary = () => {
    queryClient.invalidateQueries({ queryKey: ['library-questions'] });
    queryClient.invalidateQueries({ queryKey: ['survey-config', engagementId] });
  };
  const deleteMutation = useMutation({
    mutationFn: (id: string) => adminApi.deleteLibraryQuestion(id),
    onSuccess: invalidateLibrary,
  });
  const restoreMutation = useMutation({
    mutationFn: (id: string) => adminApi.restoreLibraryQuestion(id),
    onSuccess: invalidateLibrary,
  });
  const toggleCustomDimMutation = useMutation({
    mutationFn: ({ did, isActive }: { did: string; isActive: boolean }) =>
      adminApi.setCustomDimensionActive(engagementId, did, isActive),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['custom-questions', engagementId] });
      queryClient.invalidateQueries({ queryKey: ['admin-analysis', engagementId] });
    },
  });

  if (configQ.isLoading || libraryQ.isLoading) return <Spinner />;
  if (configQ.isError) return <p className="text-sm text-red-600">Failed to load configuration.</p>;
  if (!dims || !questions) return <Spinner />;

  // Cross-reference by question id: tells us which rows are library questions (Data /
  // Department Questions — editable/deletable) vs. the frozen standard 61 (neither).
  const libraryById = new Map((libraryQ.data?.questions ?? []).map((q) => [q.id, q]));

  const toggleDimension = (dimId: string) => {
    setDims((prev) =>
      (prev ?? []).map((d) => (d.id === dimId ? { ...d, included: !d.included } : d)),
    );
    // Also toggle all questions in this dimension
    setQuestions((prev) =>
      (prev ?? []).map((q) => {
        if (q.dimensionId === dimId) {
          const dim = dims.find((d) => d.id === dimId);
          return { ...q, included: dim ? !dim.included : q.included };
        }
        return q;
      }),
    );
    setDirty(true);
  };

  const toggleQuestion = (qId: string) => {
    setQuestions((prev) =>
      (prev ?? []).map((q) => (q.id === qId ? { ...q, included: !q.included } : q)),
    );
    setDirty(true);
  };

  const includedCount = dims.filter((d) => d.included).length;
  const includedQCount = questions.filter((q) => q.included).length;

  return (
    <div className="flex flex-col gap-8">
      {/* Custom Questions section */}
      <CustomQuestionsView engagementId={engagementId} />

      {/* Standard Question Configuration */}
      <div className="flex flex-col gap-6">
        <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-ink">Survey Configuration</h2>
          <p className="text-sm text-ink-soft">
            {includedCount} of {dims.length} dimensions · {includedQCount} of {questions.length} questions included
          </p>
        </div>
        {dirty && (
          <PrimaryButton onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
            {saveMutation.isPending ? 'Saving…' : 'Save Configuration'}
          </PrimaryButton>
        )}
      </div>

      <div className="rounded-xl border border-black/5 bg-white overflow-hidden">
        {dims.map((dim) => {
          const dimQuestions = questions.filter((q) => q.dimensionId === dim.id);
          const groups = groupBySubCategory(dimQuestions, libraryById);
          return (
            <div key={dim.id} className="border-b border-black/5 last:border-0">
              <div className="flex items-center gap-3 px-5 py-3 bg-black/[0.02]">
                <label className="flex items-center gap-3 flex-1 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={dim.included}
                    onChange={() => toggleDimension(dim.id)}
                    className="h-4 w-4 rounded border-gray-300 text-[var(--accent)] focus:ring-[var(--accent)]"
                  />
                  <span className="text-sm font-semibold text-ink">{dim.name}</span>
                  <span className="text-xs text-ink-soft">
                    ({dimQuestions.filter((q) => q.included).length}/{dimQuestions.length} questions)
                  </span>
                </label>
              </div>
              {dim.included && (
                <div className="px-5 py-2">
                  {groups.map((group, gi) => (
                    <div key={gi}>
                      {group.subCategory && (
                        <p className="pt-2 pb-1 text-[11px] font-semibold uppercase tracking-wide text-ink-soft">
                          {group.subCategory}
                        </p>
                      )}
                      <ul className="space-y-1">
                        {group.questions.map((q) => {
                          const libQ = libraryById.get(q.id);
                          return (
                            <li key={q.id}>
                              {editingId === q.id && libQ ? (
                                <LibraryQuestionEditForm
                                  question={libQ}
                                  onDone={() => {
                                    setEditingId(null);
                                    invalidateLibrary();
                                  }}
                                  onCancel={() => setEditingId(null)}
                                />
                              ) : (
                                <div className="flex items-start gap-3 py-1.5">
                                  <label className="flex flex-1 items-start gap-3 cursor-pointer">
                                    <input
                                      type="checkbox"
                                      checked={q.included}
                                      onChange={() => toggleQuestion(q.id)}
                                      className="mt-0.5 h-4 w-4 rounded border-gray-300 text-[var(--accent)] focus:ring-[var(--accent)]"
                                    />
                                    <span className="text-sm text-ink leading-snug">
                                      <span className="font-mono text-xs text-ink-soft mr-1">Q{q.externalRef}</span>
                                      {q.prompt}
                                      {libQ && !libQ.isActive && (
                                        <span className="ml-2 rounded bg-red-50 px-1.5 py-0.5 text-[10px] font-semibold text-red-600">
                                          Deleted
                                        </span>
                                      )}
                                    </span>
                                  </label>
                                  {libQ && (
                                    <div className="flex shrink-0 gap-2 pt-0.5">
                                      {libQ.isActive ? (
                                        <>
                                          <button
                                            type="button"
                                            onClick={() => setEditingId(q.id)}
                                            className="text-xs font-medium text-[var(--accent)] hover:underline"
                                          >
                                            Edit
                                          </button>
                                          <button
                                            type="button"
                                            onClick={() => deleteMutation.mutate(q.id)}
                                            disabled={deleteMutation.isPending}
                                            className="text-xs font-medium text-red-600 hover:underline"
                                          >
                                            Delete
                                          </button>
                                        </>
                                      ) : (
                                        <button
                                          type="button"
                                          onClick={() => restoreMutation.mutate(q.id)}
                                          disabled={restoreMutation.isPending}
                                          className="text-xs font-medium text-[var(--accent)] hover:underline"
                                        >
                                          Restore
                                        </button>
                                      )}
                                    </div>
                                  )}
                                </div>
                              )}
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}

        {/* Custom dimensions (e.g. "New Questions") — a checkbox here bulk-toggles every
            question under it active/inactive; per-question editing stays in CustomQuestionsView above. */}
        {(customQ.data?.customDimensions ?? []).map((cd) => {
          const cdQuestions = (customQ.data?.questions ?? []).filter((q) => q.custom_dimension_id === cd.id);
          const activeCount = cdQuestions.filter((q) => q.is_active).length;
          const allActive = cdQuestions.length > 0 && activeCount === cdQuestions.length;
          return (
            <div key={cd.id} className="border-b border-black/5 last:border-0">
              <div className="flex items-center gap-3 px-5 py-3 bg-black/[0.02]">
                <label className="flex items-center gap-3 flex-1 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={allActive}
                    onChange={() => toggleCustomDimMutation.mutate({ did: cd.id, isActive: !allActive })}
                    disabled={toggleCustomDimMutation.isPending}
                    className="h-4 w-4 rounded border-gray-300 text-[var(--accent)] focus:ring-[var(--accent)]"
                  />
                  <span className="text-sm font-semibold text-ink">{cd.name}</span>
                  <span className="text-xs text-ink-soft">
                    ({activeCount}/{cdQuestions.length} questions)
                  </span>
                </label>
              </div>
            </div>
          );
        })}
      </div>

      {saveMutation.isError && (
        <p className="text-sm text-red-600">Failed to save. Please try again.</p>
      )}
      </div>
    </div>
  );
}

// Inline edit form for a library question (prompt + sub-category + 5 anchors), mirroring
// CustomQuestionsView's QuestionForm pattern. Only ever shown for library questions — the
// standard 61 never have a `libQ` to pass in, so this form never touches them.
function LibraryQuestionEditForm({
  question,
  onDone,
  onCancel,
}: {
  question: LibraryQuestion;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [prompt, setPrompt] = useState(question.prompt);
  const [subCategory, setSubCategory] = useState(question.subCategory ?? '');
  const [anchors, setAnchors] = useState(
    [...question.anchors].sort((a, b) => a.level - b.level).map((a) => a.label),
  );
  const [saving, setSaving] = useState(false);

  const field =
    'w-full rounded-lg border border-black/10 bg-white px-3 py-2 text-sm outline-none focus:border-[var(--accent)] focus:ring-1 focus:ring-[var(--accent-soft)]';

  const save = async () => {
    if (!prompt.trim()) return;
    setSaving(true);
    const sortedAnchors = [...question.anchors].sort((a, b) => a.level - b.level);
    await adminApi.updateLibraryQuestion(question.id, {
      promptText: prompt.trim(),
      subCategory: subCategory.trim() || undefined,
      anchors: sortedAnchors.map((a, i) => ({ level: a.level, tier: a.tier, label: anchors[i] ?? a.label })),
    });
    setSaving(false);
    onDone();
  };

  return (
    <div className="rounded-xl border border-[var(--accent)] bg-[var(--accent-soft)] p-4 space-y-3">
      <div>
        <label className="block text-xs font-medium text-ink-soft mb-1">Sub-category</label>
        <input className={field} value={subCategory} onChange={(e) => setSubCategory(e.target.value)} />
      </div>
      <div>
        <label className="block text-xs font-medium text-ink-soft mb-1">Question Text</label>
        <textarea value={prompt} onChange={(e) => setPrompt(e.target.value)} className={`${field} min-h-[60px] resize-y`} />
      </div>
      <div>
        <label className="block text-xs font-medium text-ink-soft mb-1">Answer Options (Level 1–5)</label>
        <div className="space-y-1.5">
          {anchors.map((a, i) => (
            <div key={i} className="flex items-center gap-2">
              <span className="text-xs font-bold text-ink-soft w-4">{i + 1}</span>
              <input className={field} value={a} onChange={(e) => { const next = [...anchors]; next[i] = e.target.value; setAnchors(next); }} />
            </div>
          ))}
        </div>
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <button onClick={onCancel} className="rounded-lg px-3 py-1.5 text-sm font-medium text-ink-soft hover:bg-black/5">Cancel</button>
        <PrimaryButton onClick={save} disabled={saving || !prompt.trim()}>{saving ? 'Saving…' : 'Save Changes'}</PrimaryButton>
      </div>
    </div>
  );
}
